import { createContext, useContext, useEffect, useState } from "react";

const en = {
  header: {
    nav: [
      { label: "Manifesto", target: "#manifesto" },
      { label: "Services", target: "#services" },
      { label: "Portfolio", target: "#portfolio" },
      { label: "The Quiz", target: "#quiz" },
      { label: "Concierge FAQ", target: "#faq" },
    ],
    book: "Book a Consultation",
  },
  hero: {
    overline: "Interior Atelier — Dubai, UAE",
    line1: "The Art of",
    line2Italic: "Considered",
    line2: "Living",
    sub: "Bespoke interiors for the UAE's most discerning addresses — conceived, approved, and delivered by one concierge atelier.",
    cta1: "Book a Consultation",
    cta2: "Take the Feasibility Quiz",
    scroll: "Scroll",
  },
  marquee: ["Residential", "Commercial", "Turnkey", "Dubai", "Abu Dhabi", "House of Alvirah"],
  manifesto: {
    overline: "The Alvirah Standard",
    titleA: "A manifesto in",
    titleItalic: "three",
    titleB: "chapters",
    chapters: [
      {
        n: "01",
        title: "The Brief",
        copy: "Every commission begins in conversation, not catalogue. We study how you live — the rituals, the light, the objects you keep — before a single line is drawn.",
      },
      {
        n: "02",
        title: "The Craft",
        copy: "Neoclassic proportion, modern restraint, minimalist calm. Our atelier composes space the way editors compose a page — nothing accidental, nothing excess.",
      },
      {
        n: "03",
        title: "The Handover",
        copy: "Approvals, contractors, joinery, art. One concierge carries your project from first sketch to the day you turn the key — silently, precisely, on time.",
      },
    ],
    cap1: "Neoclassic — Emirates Hills",
    cap2: "Minimalist — Jumeirah Bay",
    cap3: "Turnkey — Al Hamra Villas",
  },
  portfolio: {
    overline: "Selected Works",
    titleA: "Case studies in",
    titleItalic: "considered",
    titleB: "living",
    sub: "A glimpse into recent commissions across the Emirates — each delivered approvals-to-keys by our concierge team.",
    cta: "Begin Your Own Project",
    viewCase: "View Case Study",
    backToWorks: "Back to Selected Works",
    nextProject: "Next Project",
    galleryOverline: "Room by Room",
    styleLabel: "Style",
    scopeLabel: "Scope",
    projects: [
      { slug: "elegant-elysium", n: "01", name: "Elegant Elysium", location: "Al Hamra Villas — Dubai", style: "Modern Luxury", scope: "Living · Kitchen · Dining",
        description: "A three-bedroom townhouse reimagined as a study in modern luxury — book-matched marble, smoked glass and warm bronze threading through an open-plan ground floor.",
        captions: ["Living Room", "Formal Lounge", "Guest Bathroom", "Dining Room", "Kitchen & Breakfast Bar", "Entrance Lobby", "Bathroom Vanity", "Living Room Detail"] },
      { slug: "art-deco-delights", n: "02", name: "Art Deco Delights", location: "Oceana Pacific, Palm Jumeirah", style: "Art Deco", scope: "Bedroom · Lounge",
        description: "Art Deco glamour softened for Palm Jumeirah living — scalloped headboards, fluted marble and a palette of champagne and ink.",
        captions: ["Master Bedroom", "Chaise Lounge", "Guest Bedroom", "Dressing Room", "Reading Nook", "Walk-in Closet", "Living & Dining", "Living Room"] },
      { slug: "whimsical-wonderland", n: "03", name: "Whimsical Wonderland", location: "Oceana Pacific, Palm Jumeirah", style: "Whimsical", scope: "Children's Bedroom · Play · Study",
        description: "A forest-mural wonderland for the family's youngest residents — a loft bed, play kitchen and study nook designed to grow with them.",
        captions: ["The Forest Bedroom", "Study Corner", "Play Loft", "Tea Corner", "Bedroom Nook"] },
      { slug: "timeless-traditions", n: "04", name: "Timeless Traditions", location: "Emirates Hills — Dubai", style: "Neoclassic", scope: "Staircase · Dining · Living",
        description: "Neoclassic grandeur for an Emirates Hills villa — a sculptural staircase, panelled walls and a fireplace salon composed for formal evenings.",
        captions: ["Grand Staircase", "Dining Room", "Dining Room & Sideboard", "Marble Dining Detail", "Fireplace Salon", "Living Room", "Sitting Room", "Dressing Niche", "Family Bathroom", "Dark Marble Shower"] },
      { slug: "zen-retreat", n: "05", name: "Zen Retreat", location: "Downtown Dubai", style: "Minimalist", scope: "Living · Office",
        description: "A minimalist penthouse office above Downtown — concrete, glass and low-slung furniture framing panoramic sunset views.",
        captions: ["Panoramic Lounge", "Communal Bar", "Sunset Lounge", "Executive Office", "Conference Room", "Boardroom", "Lounge", "Quiet Lounge"] },
      { slug: "sophisticated-lines", n: "06", name: "Sophisticated Lines", location: "The Pad by Omniyat, Business Bay", style: "Modern", scope: "Living · Bedroom",
        description: "Clean lines and quiet confidence for a Business Bay apartment — layered greys, smoked oak and brass accents throughout.",
        captions: ["Master Bedroom", "Living Room", "Living Room Detail", "Dining Area", "Bathroom", "Children's Room", "Dining Detail", "Kitchen & Bar"] },
      { slug: "mid-century-marvel", n: "07", name: "Mid-Century Marvel", location: "Al Majara 3, Dubai Marina", style: "Mid-Century Modern", scope: "Living · Bedroom · Dining",
        description: "Mid-century energy on the Marina — bold art, a vermilion sofa and warm walnut panelling against gallery-white walls.",
        captions: ["Living Room", "Family Lounge", "Master Bedroom", "Reading Chair", "Dining Room", "Study", "Marble Staircase", "Children's Loft"] },
      { slug: "divine-serenity", n: "08", name: "Divine Serenity", location: "Al Warqa — Dubai", style: "Classic Serene", scope: "Majlis · Dining",
        description: "A serene classical majlis in Al Warqa — coffered ceilings, sage accents and a circular chandelier anchoring family gatherings.",
        captions: ["Grand Majlis", "Family Lounge", "Entrance Doors", "Majlis Detail", "The Long Lounge", "Buffet & Serving", "Dining Room", "Dining Hall", "Bathroom Vanity", "Double Vanity"] },
    ],
    collections: { dubai: "Dubai", india: "The India Collection" },
    indiaProjects: [
      { slug: "rajendra-raj-chennai", n: "01", name: "The Rajendra Raj Residence", location: "Chennai — India", style: "Tropical Traditional", scope: "Living · Dining · Terrace",
        description: "A Chennai villa rooted in tropical craft — rattan, terracotta and teak beneath a pitched timber roof, opening out to monsoon gardens.",
        captions: ["Verandah Living Room", "Swing Lounge", "Dining Room", "Bay Window Seat", "Brass Vanity", "Reading Corner", "Terrace Pavilion"] },
      { slug: "rana-shetty-bangalore", n: "02", name: "The Shetty House", location: "JP Nagar, Bangalore — India", style: "Concrete Minimalism", scope: "Living · Bedroom · Dining",
        description: "Board-formed concrete and glass in JP Nagar — a minimalist house where light and shadow complete the composition.",
        captions: ["Concrete Living Room", "Reading Chair", "The Gallery", "Master Bedroom", "Garden Lounge", "Glass Dining Pavilion"] },
      { slug: "sinha-delhi", n: "03", name: "The Sinha Lounge", location: "Delhi NCR — India", style: "Art Deco Contemporary", scope: "Lounge · Living",
        description: "An intimate Delhi lounge in champagne and bronze — lattice screens, sculptural sconces and stone inlay details.",
        captions: ["The Lounge", "Lattice Living Room", "Coffee Table Detail", "Brass & Stone Inlay"] },
    ],
  },
  services: {
    overline: "Services",
    titleA: "Four disciplines,",
    titleB: "one",
    titleItalic: "signature",
    hint: "Hover each discipline to see the world behind it.",
    items: [
      {
        n: "I",
        title: "Residential",
        img: "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbHV4dXJ5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODYwMDQxNDZ8MA&ixlib=rb-4.1.0&q=85",
        detail: "Villas, penthouses and apartments composed around the way you live — from spatial planning to the final object placed.",
        points: ["Spatial planning", "Bespoke joinery", "Art curation"],
      },
      {
        n: "II",
        title: "Commercial",
        img: "https://images.unsplash.com/photo-1544092683-c0c9ebb368e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMGx1eHVyeSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzg2MDA0MTQ2fDA&ixlib=rb-4.1.0&q=85",
        detail: "Offices, boutiques and hospitality spaces that carry a brand's weight — engineered for DCD and landlord compliance.",
        points: ["Brand environments", "Fit-out compliance", "FF&E procurement"],
      },
      {
        n: "III",
        title: "Turnkey",
        img: "https://images.unsplash.com/photo-1700226034367-2fb120f48dfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxuZW9jbGFzc2ljJTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2MDA0MTQ2fDA&ixlib=rb-4.1.0&q=85",
        detail: "One signature, zero burden. Design, approvals, contractors and handover — delivered to you as a finished home.",
        points: ["Authority approvals", "Contractor management", "Key-ready handover"],
      },
      {
        n: "IV",
        title: "Landscaping",
        img: "/portfolio/rajendra/img-7.jpg",
        detail: "Gardens, terraces and outdoor majlis composed as rooms without ceilings — planting, shade, water and light in one considered composition.",
        points: ["Garden architecture", "Outdoor living", "Pools & terraces"],
      },
    ],
  },
  quiz: {
    overline: "The Alvirah Quiz",
    titleA: "Your preliminary estimate,",
    titleItalic: "in four steps",
    steps: [
      { key: "property_type", title: "What are we designing?", sub: "Select your property type", options: [{ v: "Villa", l: "Villa" }, { v: "Apartment", l: "Apartment" }, { v: "Penthouse", l: "Penthouse" }] },
      { key: "area", title: "Where is the address?", sub: "Select your community", options: [{ v: "Jumeirah", l: "Jumeirah" }, { v: "Downtown Dubai", l: "Downtown Dubai" }, { v: "Palm Jumeirah", l: "Palm Jumeirah" }, { v: "Dubai Hills", l: "Dubai Hills" }, { v: "Emirates Hills", l: "Emirates Hills" }, { v: "Other", l: "Other" }] },
      { key: "sqft", title: "How much canvas?", sub: "Approximate built-up area", options: [{ v: "Under 1,500 sq ft", l: "Under 1,500 sq ft" }, { v: "1,500 – 3,000 sq ft", l: "1,500 – 3,000 sq ft" }, { v: "3,000 – 6,000 sq ft", l: "3,000 – 6,000 sq ft" }, { v: "Over 6,000 sq ft", l: "Over 6,000 sq ft" }] },
      { key: "style", title: "Which language speaks to you?", sub: "Select a style direction", options: [{ v: "Modern", l: "Modern" }, { v: "Neoclassic", l: "Neoclassic" }, { v: "Minimalist", l: "Minimalist" }] },
    ],
    capture: {
      title: "Where shall we send it?",
      sub: "Download your preliminary estimate",
      name: "Full name",
      email: "Email address",
      phone: "Phone (+971 ...)",
      errName: "Please share your full name",
      errEmail: "A valid email is required",
      errPhone: "A valid phone number is required",
      errSubmit: "Something interrupted us — please try again.",
      submit: "Reveal My Estimate",
      submitting: "Preparing...",
    },
    success: {
      overline: "Your Preliminary Estimate",
      to: "to",
      text: (name, style, property, area) => `Thank you, ${name}. A detailed feasibility dossier for your ${style} ${property} in ${area} is being prepared. The next step is a private conversation.`,
      cta: "Book Your Discovery Call",
    },
    back: "Back",
    currency: "AED",
    million: "M",
    thousand: "K",
  },
  founder: {
    overline: "A Note from the Founder",
    title: "Welcome to House of Alvirah",
    paragraphs: [
      "I founded House of Alvirah with a simple belief: a home should be more than beautifully designed — it should feel deeply personal.",
      "Every space has a story to tell. Our role is to understand that story and translate it into an environment that reflects the people who live in it — their personality, lifestyle, aspirations, and sense of beauty.",
      "At House of Alvirah, we specialise in turnkey interior design, managing every aspect of the journey from concept and design to material selection, execution, styling, and final handover. Our aim is to make the process seamless, considered, and enjoyable, while ensuring that every detail is executed to the highest standard.",
      "I believe true luxury lies not simply in grandeur, but in thoughtful design — the right proportions, beautiful materials, considered details, and the feeling a space evokes when you walk through the door.",
      "Through House of Alvirah, my vision is to create interiors that are timeless, sophisticated, and distinctly yours — spaces that look beautiful, function effortlessly, and feel like home.",
      "Thank you for trusting us with your spaces, your stories, and the places you call home.",
    ],
    signoff: "With warmth,",
    name: "Saarah Alvirah Khan",
    role: "Founder & Creative Director",
    photoAlt: "Saarah Alvirah Khan, Founder & Creative Director of House of Alvirah",
    photoCap: "Saarah Alvirah Khan — Founder & Creative Director",
  },
  faq: {
    overline: "Concierge FAQ",
    titleA: "Dubai approvals,",
    titleItalic: "demystified",
    sub: "Five questions every UAE property owner asks before renovating — answered the way we'd answer them across your dining table.",
    items: [
      {
        q: "Do I need Dubai Municipality approval for my interior renovation?",
        a: "If your scope touches layout, structure, façades, or MEP systems, yes — a Dubai Municipality permit is mandatory before work begins. Purely cosmetic refreshes (paint, loose furniture) are usually exempt. The first thing our concierge does is classify your scope so you never over-permit — or worse, work without one.",
      },
      {
        q: "Why does my community or developer NOC come before any authority submission?",
        a: "Master developers such as Emaar and Nakheel require a No Objection Certificate before Dubai Municipality will even accept your file. Communities now route submissions through their own portals, with review fees, registered-contractor requirements, and refundable security deposits. We secure the NOC first, in the correct portal, so your application never bounces.",
      },
      {
        q: "When is Dubai Civil Defence approval required?",
        a: "Whenever your design affects fire and life safety — moving walls, altering exits, enclosing open plans, lowering ceilings, or touching sprinklers and alarms. DCD reviews dedicated fire-safety drawings, and their comment cycles are the most common cause of stalled projects. Our drawings are prepared to DCD code from day one.",
      },
      {
        q: "How does DEWA fit into an interior project?",
        a: "Any modification to electrical load, wiring, or plumbing requires DEWA approval and inspection. Undocumented electrical work can delay your completion certificate and even future utility connections. We coordinate load schedules, licensed contractor submissions, and final DEWA inspections as part of the turnkey scope.",
      },
      {
        q: "How long do approvals take — and what actually causes the delays?",
        a: "A well-prepared file typically clears in two to six weeks. Delays almost always come from the same five causes: submission to the wrong authority portal, incomplete document packs, unregistered consultants or contractors, scope creep into structural work, and slow responses to technical comments. Our concierge team runs DM, DCD, DEWA and developer tracks in parallel — which is precisely where weeks are saved.",
      },
    ],
  },
  booking: {
    overline: "The Discovery Call",
    titleA: "Fifteen minutes,",
    titleItalic: "privately",
    copy: "A short, unhurried conversation with our design director — your address, your ambitions, and an honest read on feasibility, timeline and investment. No obligation, no sales theatre.",
    steps: ["Select a date", "Choose a time (GST)", "Leave your details"],
    selectDate: "Select a date",
    chooseFirst: "Choose a date first",
    timesFor: (d) => `Times for ${d}`,
    name: "Full name",
    email: "Email address",
    phone: "Phone (+971 ...)",
    errName: "Please share your full name",
    errEmail: "A valid email is required",
    errPhone: "A valid phone number is required",
    errConflict: "That slot was just reserved — please choose another time.",
    errSubmit: "Something interrupted us — please try again.",
    submit: "Reserve My Call",
    submitting: "Reserving...",
    successOverline: "Your call is reserved",
    at: "at",
    successText: (email) => `A confirmation is on its way to ${email}. We will call you promptly — fifteen minutes, no more.`,
  },
  footer: {
    tagline: "A concierge interior atelier for the UAE's most considered homes and workplaces.",
    explore: "Explore",
    concierge: "Concierge",
    atelier: "Atelier",
    links: [
      { label: "Manifesto", target: "#manifesto" },
      { label: "Services", target: "#services" },
      { label: "Portfolio", target: "#portfolio" },
      { label: "The Quiz", target: "#quiz" },
      { label: "Book a Call", target: "#booking" },
    ],
    address1: "Boulevard Point, Downtown",
    address2: "Dubai, UAE",
    hours: "Sun – Thu, 10:00 – 18:00",
    rights: "© 2026 House of Alvirah. All rights reserved.",
    admin: "Atelier Admin",
  },
};

const ar = {
  header: {
    nav: [
      { label: "المانيفستو", target: "#manifesto" },
      { label: "خدماتنا", target: "#services" },
      { label: "أعمالنا", target: "#portfolio" },
      { label: "الاختبار", target: "#quiz" },
      { label: "أسئلة الكونسيرج", target: "#faq" },
    ],
    book: "احجز استشارة",
  },
  hero: {
    overline: "أتيليه تصميم داخلي — دبي، الإمارات",
    line1: "فنُّ",
    line2Italic: "العيشِ",
    line2: "بإتقان",
    sub: "تصاميم داخلية مخصصة لأرقى العناوين في الإمارات — نتولّاها من الفكرة إلى التسليم عبر أتيليه كونسيرج واحد.",
    cta1: "احجز استشارة",
    cta2: "خذ اختبار الجدوى",
    scroll: "مرّر",
  },
  marquee: ["سكني", "تجاري", "تسليم مفتاح", "دبي", "أبوظبي", "هاوس أوف ألفيرا"],
  manifesto: {
    overline: "معيار ألفيرا",
    titleA: "مانيفستو في",
    titleItalic: "ثلاثة",
    titleB: "فصول",
    chapters: [
      {
        n: "٠١",
        title: "الموجز",
        copy: "كل تكليف يبدأ بالحديث لا بالكتالوج. ندرس كيف تعيش — طقوسك، الضوء، الأشياء التي تحتفظ بها — قبل أن نرسم خطاً واحداً.",
      },
      {
        n: "٠٢",
        title: "الحِرفة",
        copy: "نِسب كلاسيكية جديدة، وتحفّظ عصري، وهدوء بسيط. يؤلّف أتيليهنا الفراغ كما يؤلّف المحرر صفحته — لا شيء عرضياً، ولا شيء زائد.",
      },
      {
        n: "٠٣",
        title: "التسليم",
        copy: "الموافقات، المقاولون، الأعمال الخشبية، الفن. كونسيرج واحد يحمل مشروعك من أول مخطط إلى يوم تدير فيه المفتاح — بصمت ودقة والتزام بالموعد.",
      },
    ],
    cap1: "نيوكلاسيك — تلال الإمارات",
    cap2: "مينيمال — جميرا باي",
    cap3: "تسليم مفتاح — فلل الحمرا",
  },
  portfolio: {
    overline: "أعمال مختارة",
    titleA: "دراسات حالة في",
    titleItalic: "فن",
    titleB: "العيش",
    sub: "لمحة عن أحدث مشاريعنا في أنحاء الإمارات — كل منها سُلّم من الموافقات إلى المفتاح عبر فريق الكونسيرج لدينا.",
    cta: "ابدأ مشروعك الخاص",
    viewCase: "شاهد دراسة الحالة",
    backToWorks: "العودة إلى الأعمال المختارة",
    nextProject: "المشروع التالي",
    galleryOverline: "غرفةً بغرفة",
    styleLabel: "الأسلوب",
    scopeLabel: "النطاق",
    projects: [
      { slug: "elegant-elysium", n: "٠١", name: "Elegant Elysium", location: "فلل الحمرا — دبي", style: "فخامة عصرية", scope: "معيشة · مطبخ · طعام",
        description: "تاون هاوس من ثلاث غرف أُعيد تصوره كدراسة في الفخامة العصرية — رخام متطابق الألياف وزجاج مدخّن وبرونز دافئ يمتد عبر طابق مفتوح.",
        captions: ["غرفة المعيشة", "الصالة الرسمية", "حمّام الضيوف", "غرفة الطعام", "المطبخ وركن الإفطار", "مدخل الاستقبال", "مغسلة الحمّام", "تفاصيل المعيشة"] },
      { slug: "art-deco-delights", n: "٠٢", name: "Art Deco Delights", location: "أوشيانا باسيفيك، نخلة جميرا", style: "آرت ديكو", scope: "غرف نوم · صالة",
        description: "بريق الآرت ديكو بنعومة تناسب الحياة في نخلة جميرا — أسرّة مقوسة ورخام مخدد ولوحة ألوان من الشمبانيا والحبر.",
        captions: ["غرفة النوم الرئيسية", "ركن الاستلقاء", "غرفة نوم الضيوف", "غرفة الملابس", "ركن القراءة", "الخزانة المفتوحة", "المعيشة والطعام", "غرفة المعيشة"] },
      { slug: "whimsical-wonderland", n: "٠٣", name: "Whimsical Wonderland", location: "أوشيانا باسيفيك، نخلة جميرا", style: "عالم الأطفال", scope: "غرفة أطفال · لعب · دراسة",
        description: "عالم ساحر بجداريات الغابة لصغار العائلة — سرير علوي ومطبخ لعب وركن دراسة صُمم ليكبر معهم.",
        captions: ["غرفة نوم الغابة", "ركن الدراسة", "علّية اللعب", "ركن الشاي", "ركن النوم"] },
      { slug: "timeless-traditions", n: "٠٤", name: "Timeless Traditions", location: "تلال الإمارات — دبي", style: "نيوكلاسيك", scope: "مدخل · طعام · معيشة",
        description: "فخامة نيوكلاسيكية لفيلا في تلال الإمارات — درج منحوت وجدران مكسوة وصالون مدفأة أُعد لأمسيات رسمية.",
        captions: ["الدرج الكبير", "غرفة الطعام", "الطعام والخزانة", "تفاصيل طاولة الرخام", "صالون المدفأة", "غرفة المعيشة", "غرفة الجلوس", "ركن التزيين", "حمّام العائلة", "دش الرخام الداكن"] },
      { slug: "zen-retreat", n: "٠٥", name: "Zen Retreat", location: "داون تاون دبي", style: "مينيمال", scope: "معيشة · مكتب",
        description: "مكتب بنتهاوس مينيمال فوق داون تاون — خرسانة وزجاج وأثاث منخفض يؤطر إطلالات الغروب.",
        captions: ["الصالة البانورامية", "البار المشترك", "صالة الغروب", "المكتب التنفيذي", "قاعة المؤتمرات", "قاعة الاجتماعات", "الصالة", "الصالة الهادئة"] },
      { slug: "sophisticated-lines", n: "٠٦", name: "Sophisticated Lines", location: "ذا باد من أمنيات، الخليج التجاري", style: "عصري", scope: "معيشة · نوم",
        description: "خطوط نظيفة وثقة هادئة لشقة في الخليج التجاري — طبقات رمادية وبلوط مدخن ولمسات نحاسية.",
        captions: ["غرفة النوم الرئيسية", "غرفة المعيشة", "تفاصيل المعيشة", "ركن الطعام", "الحمّام", "غرفة الأطفال", "تفاصيل الطعام", "المطبخ والبار"] },
      { slug: "mid-century-marvel", n: "٠٧", name: "Mid-Century Marvel", location: "المجرة ٣، دبي مارينا", style: "ميد سينشري", scope: "معيشة · نوم · طعام",
        description: "طاقة منتصف القرن على المارينا — فن جريء وأريكة قرمزية وألواح جوز دافئة على جدران بيضاء.",
        captions: ["غرفة المعيشة", "صالة العائلة", "غرفة النوم الرئيسية", "كرسي القراءة", "غرفة الطعام", "المكتب المنزلي", "درج الرخام", "علّية الأطفال"] },
      { slug: "divine-serenity", n: "٠٨", name: "Divine Serenity", location: "الورقاء — دبي", style: "كلاسيكي هادئ", scope: "مجلس · طعام",
        description: "مجلس كلاسيكي هادئ في الورقاء — أسقف مزخرفة ولمسات خضراء مريحة وثريا دائرية تجمع العائلة.",
        captions: ["المجلس الكبير", "صالة العائلة", "أبواب المدخل", "تفاصيل المجلس", "الصالة الطويلة", "ركن التقديم", "غرفة الطعام", "قاعة الطعام", "مغسلة الحمّام", "المغسلة المزدوجة"] },
    ],
    collections: { dubai: "دبي", india: "المجموعة الهندية" },
    indiaProjects: [
      { slug: "rajendra-raj-chennai", n: "٠١", name: "The Rajendra Raj Residence", location: "تشيناي — الهند", style: "تقليدي استوائي", scope: "معيشة · طعام · تراس",
        description: "فيلا في تشيناي متجذرة في الحرفة الاستوائية — الروطان والطين والساج تحت سقف خشبي مائل، تنفتح على حدائق الموسم.",
        captions: ["غرفة معيشة الشرفة", "صالة الأرجوحة", "غرفة الطعام", "مقعد النافذة البارزة", "المغسلة النحاسية", "ركن القراءة", "جناح التراس"] },
      { slug: "rana-shetty-bangalore", n: "٠٢", name: "The Shetty House", location: "جاي بي ناجار، بنغالور — الهند", style: "مينيمال الخرسانة", scope: "معيشة · نوم · طعام",
        description: "خرسانة مصبوبة وزجاج في جاي بي ناجار — منزل مينيمال يكتمل بالضوء والظل.",
        captions: ["غرفة المعيشة الخرسانية", "كرسي القراءة", "الرواق", "غرفة النوم الرئيسية", "صالة الحديقة", "جناح الطعام الزجاجي"] },
      { slug: "sinha-delhi", n: "٠٣", name: "The Sinha Lounge", location: "دلهي إن سي آر — الهند", style: "آرت ديكو معاصر", scope: "صالة · معيشة",
        description: "صالة دلهي حميمة بألوان الشمبانيا والبرونز — شاشات مخرمة وثريات جدارية منحوتة وتفاصيل ترصيع حجرية.",
        captions: ["الصالة", "غرفة المعيشة المخرمة", "تفاصيل طاولة القهوة", "ترصيع النحاس والحجر"] },
    ],
  },
  services: {
    overline: "خدماتنا",
    titleA: "أربعة تخصصات،",
    titleB: "توقيع",
    titleItalic: "واحد",
    hint: "مرّر فوق كل تخصص لاكتشاف عالمه.",
    items: [
      {
        n: "I",
        title: "سكني",
        img: "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbHV4dXJ5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODYwMDQxNDZ8MA&ixlib=rb-4.1.0&q=85",
        detail: "فلل وبنتهاوس وشقق مؤلَّفة حول أسلوب حياتك — من التخطيط الفراغي إلى آخر قطعة تُوضع.",
        points: ["التخطيط الفراغي", "أعمال خشبية مخصصة", "تنسيق الفن"],
      },
      {
        n: "II",
        title: "تجاري",
        img: "https://images.unsplash.com/photo-1544092683-c0c9ebb368e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMGx1eHVyeSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzg2MDA0MTQ2fDA&ixlib=rb-4.1.0&q=85",
        detail: "مكاتب وبوتيكات ومساحات ضيافة تحمل وزن علامتك — مهندَسة للامتثال للدفاع المدني ومتطلبات المالك.",
        points: ["بيئات العلامة", "امتثال التشطيبات", "توريد الأثاث"],
      },
      {
        n: "III",
        title: "تسليم مفتاح",
        img: "https://images.unsplash.com/photo-1700226034367-2fb120f48dfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxuZW9jbGFzc2ljJTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2MDA0MTQ2fDA&ixlib=rb-4.1.0&q=85",
        detail: "توقيع واحد، ولا عبء عليك. التصميم والموافقات والمقاولون والتسليم — نسلّمها لك منزلاً جاهزاً.",
        points: ["موافقات الجهات", "إدارة المقاولين", "تسليم جاهز للسكن"],
      },
      {
        n: "IV",
        title: "تنسيق الحدائق",
        img: "/portfolio/rajendra/img-7.jpg",
        detail: "حدائق وتراسات ومجالس خارجية تُصمم كغرف بلا أسقف — الزراعة والظل والماء والضوء في تكوين واحد متأنٍّ.",
        points: ["هندسة الحدائق", "المعيشة الخارجية", "المسابح والتراسات"],
      },
    ],
  },
  quiz: {
    overline: "اختبار ألفيرا",
    titleA: "تقديرك المبدئي",
    titleItalic: "في أربع خطوات",
    steps: [
      { key: "property_type", title: "ماذا سنصمم؟", sub: "اختر نوع العقار", options: [{ v: "Villa", l: "فيلا" }, { v: "Apartment", l: "شقة" }, { v: "Penthouse", l: "بنتهاوس" }] },
      { key: "area", title: "أين يقع العقار؟", sub: "اختر منطقتك", options: [{ v: "Jumeirah", l: "جميرا" }, { v: "Downtown Dubai", l: "داون تاون دبي" }, { v: "Palm Jumeirah", l: "نخلة جميرا" }, { v: "Dubai Hills", l: "دبي هيلز" }, { v: "Emirates Hills", l: "تلال الإمارات" }, { v: "Other", l: "أخرى" }] },
      { key: "sqft", title: "ما مساحة اللوحة؟", sub: "المساحة المبنية التقريبية", options: [{ v: "Under 1,500 sq ft", l: "أقل من 1,500 قدم²" }, { v: "1,500 – 3,000 sq ft", l: "1,500 – 3,000 قدم²" }, { v: "3,000 – 6,000 sq ft", l: "3,000 – 6,000 قدم²" }, { v: "Over 6,000 sq ft", l: "أكثر من 6,000 قدم²" }] },
      { key: "style", title: "أي أسلوب يخاطبك؟", sub: "اختر اتجاه التصميم", options: [{ v: "Modern", l: "عصري" }, { v: "Neoclassic", l: "نيوكلاسيك" }, { v: "Minimalist", l: "مينيمال" }] },
    ],
    capture: {
      title: "أين نرسل التقدير؟",
      sub: "احصل على تقديرك المبدئي",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف (+971 ...)",
      errName: "يرجى مشاركة اسمك الكامل",
      errEmail: "البريد الإلكتروني غير صالح",
      errPhone: "رقم الهاتف غير صالح",
      errSubmit: "حدث خلل ما — يرجى المحاولة مجدداً.",
      submit: "اكشف تقديري",
      submitting: "جارٍ التحضير...",
    },
    success: {
      overline: "تقديرك المبدئي",
      to: "إلى",
      text: (name, style, property, area) => `شكراً لك يا ${name}. نُعدّ ملف الجدوى المفصل لـ${property} بأسلوب ${style} في ${area}. الخطوة التالية حديث خاص.`,
      cta: "احجز مكالمة التعارف",
    },
    back: "رجوع",
    currency: "د.إ",
    million: "مليون",
    thousand: "ألف",
  },
  founder: {
    overline: "رسالة من المؤسِّسة",
    title: "مرحباً بكم في هاوس أوف ألفيرا",
    paragraphs: [
      "أسستُ هاوس أوف ألفيرا إيماناً بقناعة بسيطة: المنزل يجب أن يكون أكثر من مجرد تصميم جميل — يجب أن يبدو شخصياً بعمق.",
      "لكل مساحة قصة ترويها. دورنا أن نفهم تلك القصة ونترجمها إلى بيئة تعكس الأشخاص الذين يعيشون فيها — شخصياتهم وأسلوب حياتهم وتطلعاتهم وحسّهم الجمالي.",
      "في هاوس أوف ألفيرا، نتخصص في التصميم الداخلي بنظام التسليم المفتاح، وندير كل مرحلة من الرحلة — من المفهوم والتصميم إلى اختيار المواد والتنفيذ والتنسيق والتسليم النهائي. هدفنا أن نجعل العملية سلسة ومدروسة وممتعة، مع ضمان تنفيذ كل تفصيل بأعلى معايير الجودة.",
      "أؤمن أن الفخامة الحقيقية لا تكمن في العظمة وحدها، بل في التصميم المدروس — النِّسب الصحيحة، والمواد الجميلة، والتفاصيل المتأنية، والشعور الذي تبعثه المساحة فيك لحظة عبورك الباب.",
      "ومن خلال هاوس أوف ألفيرا، رؤيتي أن أبتكر تصاميم داخلية خالدة وراقية وخاصة بك وحدك — مساحات جميلة المنظر، سلسة الاستخدام، وتشعر فيها وكأنك في بيتك.",
      "شكراً لثقتكم بنا في مساحاتكم وقصصكم والأماكن التي تسمّونها بيتاً.",
    ],
    signoff: "بكل المودة،",
    name: "سارة ألفيرا خان",
    role: "المؤسِّسة والمديرة الإبداعية",
    photoAlt: "سارة ألفيرا خان، المؤسِّسة والمديرة الإبداعية لهاوس أوف ألفيرا",
    photoCap: "سارة ألفيرا خان — المؤسِّسة والمديرة الإبداعية",
  },
  faq: {
    overline: "أسئلة الكونسيرج",
    titleA: "موافقات دبي،",
    titleItalic: "بوضوح تام",
    sub: "خمسة أسئلة يطرحها كل مالك عقار في الإمارات قبل التجديد — نجيب عنها كما نجيب عليها على مائدتك.",
    items: [
      {
        q: "هل أحتاج موافقة بلدية دبي لتجديد التصميم الداخلي؟",
        a: "إذا كان نطاق العمل يمس المخطط أو الهيكل أو الواجهات أو الأنظمة الكهروميكانيكية، فنعم — تصريح بلدية دبي إلزامي قبل بدء الأعمال. أما التحديثات التجميلية البحتة (الدهان، الأثاث المنقول) فهي معفاة غالباً. أول ما يفعله الكونسيرج لدينا هو تصنيف نطاقك، فلا تدفع لتصاريح زائدة — والأسوأ أن تعمل دونها.",
      },
      {
        q: "لماذا تأتي شهادة عدم الممانعة من المطوّر قبل أي تقديم رسمي؟",
        a: "يشترط كبار المطورين مثل إعمار ونخيل شهادة عدم ممانعة قبل أن تقبل بلدية دبي ملفك أصلاً. وباتت المجمعات تستقبل الطلبات عبر بواباتها الخاصة، مع رسوم مراجعة واشتراط مقاولين مسجلين وودائع ضمان مستردة. نؤمّن الشهادة أولاً وعبر البوابة الصحيحة، فلا يرتد طلبك إليك.",
      },
      {
        q: "متى تلزم موافقة الدفاع المدني بدبي؟",
        a: "كلما مسّ التصميم السلامة من الحريق — نقل الجدران، تعديل المخارج، إغلاق المخططات المفتوحة، خفض الأسقف، أو لمس أنظمة الإنذار والرشاشات. يراجع الدفاع المدني مخططات مخصصة للسلامة، ودورات تعليقاته هي السبب الأشهر لتعثر المشاريع. نعدّ مخططاتنا وفق كود الدفاع المدني منذ اليوم الأول.",
      },
      {
        q: "ما دور هيئة كهرباء ومياه دبي (ديوا) في مشروع داخلي؟",
        a: "أي تعديل على الأحمال الكهربائية أو التمديدات أو السباكة يستلزم موافقة ديوا وتفتيشها. والأعمال الكهربائية غير الموثقة قد تؤخر شهادة الإنجاز وحتى توصيلات الخدمة مستقبلاً. ننسّق جداول الأحمال وتقديمات المقاولين المرخصين وتفتيش ديوا النهائي ضمن نطاق التسليم المفتاح.",
      },
      {
        q: "كم تستغرق الموافقات — وما أسباب التأخير فعلاً؟",
        a: "الملف المعدّ جيداً يُنجز عادة بين أسبوعين وستة. والتأخير يعود دائماً تقريباً لخمسة أسباب: التقديم عبر البوابة الخاطئة، ونقص المستندات، واستشاريون أو مقاولون غير مسجلين، وتوسع النطاق نحو أعمال إنشائية، وبطء الرد على الملاحظات الفنية. يدير فريق الكونسيرج مسارات البلدية والدفاع المدني وديوا والمطوّر بالتوازي — وهنا تحديداً تُختصر الأسابيع.",
      },
    ],
  },
  booking: {
    overline: "مكالمة التعارف",
    titleA: "خمس عشرة دقيقة،",
    titleItalic: "بخصوصية",
    copy: "حديث قصير هادئ مع مدير التصميم — عنوانك، طموحاتك، وقراءة صادقة للجدوى والجدول والاستثمار. بلا التزام، وبلا استعراض بيعي.",
    steps: ["اختر التاريخ", "اختر الوقت (بتوقيت الخليج)", "اترك بياناتك"],
    selectDate: "اختر تاريخاً",
    chooseFirst: "اختر التاريخ أولاً",
    timesFor: (d) => `الأوقات المتاحة — ${d}`,
    name: "الاسم الكامل",
    email: "البريد الإلكتروني",
    phone: "رقم الهاتف (+971 ...)",
    errName: "يرجى مشاركة اسمك الكامل",
    errEmail: "البريد الإلكتروني غير صالح",
    errPhone: "رقم الهاتف غير صالح",
    errConflict: "حُجز هذا الموعد للتو — يرجى اختيار وقت آخر.",
    errSubmit: "حدث خلل ما — يرجى المحاولة مجدداً.",
    submit: "احجز مكالمتي",
    submitting: "جارٍ الحجز...",
    successOverline: "تم حجز مكالمتك",
    at: "الساعة",
    successText: (email) => `التأكيد في طريقه إلى ${email}. سنتصل بك في الموعد تماماً — خمس عشرة دقيقة، لا أكثر.`,
  },
  footer: {
    tagline: "أتيليه كونسيرج للتصميم الداخلي لأرقى البيوت وأماكن العمل في الإمارات.",
    explore: "استكشف",
    concierge: "الكونسيرج",
    atelier: "الأتيليه",
    links: [
      { label: "المانيفستو", target: "#manifesto" },
      { label: "خدماتنا", target: "#services" },
      { label: "أعمالنا", target: "#portfolio" },
      { label: "الاختبار", target: "#quiz" },
      { label: "احجز مكالمة", target: "#booking" },
    ],
    address1: "بوليفارد بوينت، داون تاون",
    address2: "دبي، الإمارات",
    hours: "الأحد – الخميس، 10:00 – 18:00",
    rights: "© 2026 هاوس أوف ألفيرا. جميع الحقوق محفوظة.",
    admin: "إدارة الأتيليه",
  },
};

const dictionaries = { en, ar };
const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("alvirah_lang") || "en");

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("alvirah_lang", lang);
  }, [lang]);

  const toggle = () => setLang((l) => (l === "en" ? "ar" : "en"));

  return (
    <LangContext.Provider value={{ lang, copy: dictionaries[lang], toggle }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
